/** MODELO TÉCNICO ÚNICO (Fase 5 — Etapa 3)
 *  MODULADOR → MODELO TÉCNICO → VISTA TÉCNICA → ANEXO PDF: mismos módulos, medidas,
 *  cotas, uniones, ángulos, herrajes y posiciones en todas las vistas. */

export const PAD = 90;

export const derivarModulos = (receta, familia) => {
  const r = receta || {};
  const alto = Number(r.vano_alto || 0) || 2000;
  const fijos = familia === 'pabm' ? (r.fijos || []) : [];
  const mods = [];
  fijos.forEach((x, i) => {
    const m = { id: `fijo_${x.posicion || 'der'}_${i + 1}`, tipo: 'VTF', ancho_mm: Number(x.vano_ancho || 0) || 500,
      alto_mm: Number(x.vano_alto || 0) || alto, orientacion: x.posicion || 'derecha', idx: i, barra: !!x.barra };
    if (x.posicion === 'izquierda') mods.push(m); else mods.splice(mods.length + 1, 0, m);
  });
  const izq = mods.filter((m) => m.orientacion === 'izquierda');
  const der = mods.filter((m) => m.orientacion !== 'izquierda');
  const puerta = familia === 'pabm'
    ? [{ id: 'puerta', tipo: 'PABM', ancho_mm: Number(r.vano_ancho || 0) || 700, alto_mm: alto,
         apertura: r.apertura || 'derecha', embisagrado: r.embisagrado || 'muro' }]
    : [{ id: 'fijo', tipo: 'VTF', ancho_mm: Number(r.vano_ancho || 0) || 700, alto_mm: alto, barra: !!r.barra_estabilizadora }];
  return [...izq, ...puerta, ...der];
};

// DETECCIÓN AUTOMÁTICA POR GEOMETRÍA (Fase 6.1): propone uniones para pares adyacentes y extremos contra muro.
// Devuelve SOLO uniones NUEVAS (las existentes nunca se modifican) — la geometría propone, el usuario confirma/edita.
export const detectarUniones = (receta, familia) => {
  const r = receta || {};
  if (!['pabm', 'vtf'].includes(familia)) return [];
  const mods = derivarModulos(r, familia);
  const exist = r.uniones || [];
  const yaHay = (a, b) => exist.some((u) => (u.componente_a === a && u.componente_b === b) || (u.componente_a === b && u.componente_b === a));
  const emb = r.embisagrado || 'muro';
  const embFijo = emb.startsWith('fijo');
  const apertura = r.apertura || 'derecha';
  const base = { tipo_herraje: 'auto', cantidad: '', acabado: '', observaciones: '', holgura_utilizada: '', auto: true, auto_detectada: true };
  const out = [];
  for (let i = 0; i < mods.length - 1; i++) {
    const A = mods[i], B = mods[i + 1];
    if (yaHay(A.id, B.id)) continue;
    const pf = A.id === 'puerta' || B.id === 'puerta';
    if (pf) {
      if (!embFijo) continue;  // puerta a MURO: bisagras muro-vidrio por configuración — SIN bisagra v-v (regla sagrada, no duplicar)
      const fijoIzq = B.id === 'puerta';  // el fijo está a la izquierda de la puerta
      const ladoBisagra = (apertura === 'derecha' && fijoIzq) || (apertura === 'izquierda' && !fijoIzq);
      out.push({ ...base, componente_a: A.id, componente_b: B.id, tipo_union: 'puerta_fijo',
        angulo: ladoBisagra ? emb.replace('fijo_', '') : '180', lado_bisagra: ladoBisagra });
    } else {
      const ang = (A.orientacion === 'L' || B.orientacion === 'L') ? '90' : '180';
      out.push({ ...base, componente_a: A.id, componente_b: B.id, tipo_union: 'fijo_fijo', angulo: ang });
    }
  }
  // extremos contra MURO — solo si el módulo del extremo es un FIJO (la puerta a muro ya trae sus bisagras muro-vidrio)
  const muroYa = (id, lado) => exist.some((u) => ((u.componente_a === 'muro' && u.componente_b === id) || (u.componente_b === 'muro' && u.componente_a === id)) && (u.lado_muro || 'izq') === lado);
  const prim = mods[0], ult = mods[mods.length - 1];
  if (prim && prim.tipo === 'VTF' && (r.lado_izq || 'muro') === 'muro' && !muroYa(prim.id, 'izq')) {
    out.push({ ...base, componente_a: prim.id, componente_b: 'muro', tipo_union: 'fijo_muro', angulo: '', lado_muro: 'izq' });
  }
  if (ult && ult.tipo === 'VTF' && (r.lado_der || 'muro') === 'muro' && !muroYa(ult.id, 'der')) {
    out.push({ ...base, componente_a: ult.id, componente_b: 'muro', tipo_union: 'fijo_muro', angulo: '', lado_muro: 'der' });
  }
  return out;
};

export const derivarModeloTecnico = (receta, familia) => {
  const r = receta || {};
  const mods = derivarModulos(r, familia);
  const totalW = mods.reduce((a, m) => a + m.ancho_mm, 0);
  const totalH = Math.max(0, ...mods.map((m) => m.alto_mm)) || 2000;
  let x = PAD, nv = 0;
  const modulos = mods.map((m) => {
    const o = { ...m, x, y: PAD + (totalH - m.alto_mm),
      etiqueta: m.tipo === 'PABM' ? 'PABM-01' : `VTF-${String(++nv).padStart(2, '0')}`,
      nBis: Number(r.bisagras_cantidad || 0) || (m.alto_mm > 1800 ? 3 : 2) };
    x += m.ancho_mm;
    return o;
  });
  const uniones = (r.uniones || []).map((u, i) => {
    const muro = u.componente_a === 'muro' || u.componente_b === 'muro';
    const A = modulos.find((m) => m.id === u.componente_a), B = modulos.find((m) => m.id === u.componente_b);
    const pf = !muro && (u.componente_a === 'puerta' || u.componente_b === 'puerta');
    const ang = String(u.angulo || '90').replace('°', '');
    let punto = { x: PAD + totalW / 2, y: PAD - 40 };
    if (muro) {
      const M = A || B;
      if (M) punto = { x: (u.lado_muro === 'der') ? M.x + M.ancho_mm : M.x, y: PAD + totalH * 0.25 };
    } else if (A && B) { const [L, R] = A.x <= B.x ? [A, B] : [B, A]; punto = { x: (L.x + L.ancho_mm + R.x) / 2, y: PAD + totalH / 2 }; }
    return { ...u, _i: i, etiqueta: `U${i + 1}`, es_muro: muro, punto, angulo_norm: muro ? '' : ang,
      etiqueta_tecnica: muro ? 'HERRAJE FIJO-CANCEL VIDRIO-MURO' : `${pf ? 'BISAGRA' : 'HERRAJE'} VIDRIO-VIDRIO ${ang}°` };
  });
  const herrajes = (r.herrajes_visuales || []).map((h, i) => ({ ...h, etiqueta: h.etiqueta || `H${i + 1}` }));
  // PLANTA (vista superior): recorrido de segmentos con giros reales según ángulo de cada unión
  const planta = [];
  if (modulos.length > 1) {
    let px = 0, py = 0, dir = 0;
    modulos.forEach((m, i) => {
      const rad = (dir * Math.PI) / 180;
      const nx = px + Math.cos(rad) * m.ancho_mm, ny = py + Math.sin(rad) * m.ancho_mm;
      planta.push({ tipo: 'seg', x1: px, y1: py, x2: nx, y2: ny, etiqueta: m.etiqueta, largo: m.ancho_mm, esPuerta: m.tipo === 'PABM' });
      px = nx; py = ny;
      const sig = modulos[i + 1];
      if (sig) {
        const u = uniones.find((uu) => (uu.componente_a === m.id && uu.componente_b === sig.id) || (uu.componente_b === m.id && uu.componente_a === sig.id));
        const ang = u ? Number(u.angulo_norm) : 180;
        planta.push({ tipo: 'giro', x: px, y: py, angulo: ang, etiqueta: u ? u.etiqueta_tecnica : '' });
        dir += 180 - ang;
      }
    });
  }
  return { modulos, uniones, herrajes, totalW, totalH, planta,
    tieneGiro: uniones.some((u) => !u.es_muro && u.angulo_norm !== '180'),
    conjunto: familia === 'pabm' && modulos.length > 1 ? `1 puerta + ${modulos.length - 1} fijo(s)` : '' };
};
